import { fetchQuery, graphql } from 'react-relay';
import type { IEnvironment } from 'relay-runtime';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import { fireOperationalEvent } from '@atlassian/ui-modifications-analytics/utils/fire-operational-event';

import type { LookupProjects } from '../../common/types/lookup';
import { ErrorWithStatusCode } from '../../common/utils/errors/error-with-status-code';
import type {
	fetchProjectsQuery,
	fetchProjectsQuery$data,
} from './__generated__/fetchProjectsQuery.graphql';

export const fetchProjects = async (
	environment: IEnvironment,
	projectIds: string[],
	createAnalyticsEvent: CreateUIAnalyticsEvent,
): Promise<LookupProjects> => {
	try {
		const apiStartTime = performance.now();
		const response: fetchProjectsQuery$data | undefined = await fetchQuery<fetchProjectsQuery>(
			environment,
			/* eslint-disable @atlassian/relay/unused-fields */
			graphql`
				query fetchProjectsQuery($projectIds: [ID!]!) {
					jira {
						jiraProjects(ids: $projectIds) {
							id
							projectId
							key
							name
							avatar {
								large
							}
						}
					}
				}
			`,
			{ projectIds },
			{
				networkCacheConfig: { force: false },
				fetchPolicy: 'store-or-network',
			},
		).toPromise();

		if (!response?.jira?.jiraProjects) {
			throw new ErrorWithStatusCode(
				'[fetch-projects service] [fetchProjectsQuery]: Property jira.jiraProjects does not exist.',
				{
					cause: 'FETCH_PROJECTS_MISSING_PROJECTS_PROPERTY',
				},
				'FETCH_PROJECTS_MISSING_PROJECTS_PROPERTY',
			);
		}

		const apiLatencyMs = Math.floor(performance.now() - apiStartTime);

		fireOperationalEvent(createAnalyticsEvent, {
			action: 'success',
			actionSubject: 'jiraUiModificationsFetchProjects',
			attributes: {
				projectsAPILatency: apiLatencyMs,
			},
		});

		return response.jira.jiraProjects
			.filter(
				(project): project is NonNullable<typeof project> & { projectId: string } =>
					project !== null &&
					project !== undefined &&
					project.projectId !== null &&
					project.projectId !== undefined,
			)
			.map((project) => ({
				// Use projectId (numeric ID like "10001"), not id (ARI)
				id: project.projectId,
				key: project.key,
				name: project.name,
				avatarUrls: {
					'48x48': project.avatar?.large || '',
				},
			}));
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (err: any) {
		if (!err.cause) {
			err.cause = 'FETCH_PROJECTS_UNKNOWN_ERROR';
		}

		throw err;
	}
};
